import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ConfigProvider,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Skeleton,
  Tooltip,
  message,
  theme as antdTheme,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  KeyOutlined,
  LockOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import {
  serviceSecretCreate,
  serviceSecretDelete,
  serviceSecretsGet,
  serviceSecretReveal,
  serviceSecretUpdate,
  type ServiceSecretDetail,
  type ServiceSecretFields,
  type ServiceSecretInput,
  type ServiceSecretSummary,
  type ServiceSecretValues,
} from '@/services/treasure';
import styles from './ServiceSecretsTab.module.css';

interface Props {
  mobile?: boolean;
}

type DetailFieldKey = keyof ServiceSecretFields | keyof ServiceSecretValues;
type ServiceSecretFormValues = ServiceSecretFields & ServiceSecretValues;
type UnlockAction = 'view' | 'edit';

interface UnlockRequest {
  id: string;
  name: string;
  action: UnlockAction;
}

interface SecretGroup {
  key: string;
  name: string;
  items: ServiceSecretSummary[];
}

const RECORD_FIELDS: {
  key: Exclude<keyof ServiceSecretFields, 'serviceName'>;
  label: string;
  mono?: boolean;
}[] = [
  { key: 'requestPublicUrl', label: '请求公网地址', mono: true },
  { key: 'requestInternalUrl', label: '请求内网地址', mono: true },
  { key: 'groupName', label: '分组名称' },
  { key: 'tenantName', label: '租户名称' },
  { key: 'applicationName', label: '应用名称' },
  { key: 'appId', label: 'APP ID', mono: true },
  { key: 'clientId', label: 'Client ID', mono: true },
];

const SECRET_FIELDS = [
  { key: 'accessKey', label: 'Access Key', flag: 'hasAccessKey' },
  { key: 'secretKey', label: 'Secret Key', flag: 'hasSecretKey' },
  { key: 'personalToken', label: 'Personal Token', flag: 'hasPersonalToken' },
  { key: 'apiKey', label: 'API KEY', flag: 'hasApiKey' },
] as const;

const DETAIL_FIELDS: { key: DetailFieldKey; label: string; mono?: boolean }[] = [
  { key: 'serviceName', label: '服务名称' },
  { key: 'requestPublicUrl', label: '请求公网地址', mono: true },
  { key: 'requestInternalUrl', label: '请求内网地址', mono: true },
  { key: 'groupName', label: '分组名称' },
  { key: 'tenantName', label: '租户名称' },
  { key: 'applicationName', label: '应用名称' },
  { key: 'appId', label: 'APP ID', mono: true },
  { key: 'clientId', label: 'Client ID', mono: true },
  { key: 'accessKey', label: 'Access Key', mono: true },
  { key: 'secretKey', label: 'Secret Key', mono: true },
  { key: 'personalToken', label: 'Personal Token', mono: true },
  { key: 'apiKey', label: 'API KEY', mono: true },
];

const FORM_FIELDS: {
  key: DetailFieldKey;
  label: string;
  placeholder: string;
  secret?: boolean;
  mono?: boolean;
}[] = [
  {
    key: 'serviceName',
    label: '服务名称',
    placeholder: '例如：星链数据服务',
  },
  {
    key: 'requestPublicUrl',
    label: '请求公网地址',
    placeholder: '例如：https://api.example.com',
    mono: true,
  },
  {
    key: 'requestInternalUrl',
    label: '请求内网地址',
    placeholder: '例如：http://10.0.0.12:8080',
    mono: true,
  },
  { key: 'groupName', label: '分组名称', placeholder: '例如：量化平台' },
  { key: 'tenantName', label: '租户名称', placeholder: '例如：Aden Research' },
  { key: 'applicationName', label: '应用名称', placeholder: '例如：行情同步服务' },
  { key: 'appId', label: 'APP ID', placeholder: '可选', mono: true },
  { key: 'clientId', label: 'Client ID', placeholder: '可选', mono: true },
  { key: 'accessKey', label: 'Access Key', placeholder: '可选，留空不保存', secret: true },
  { key: 'secretKey', label: 'Secret Key', placeholder: '可选，留空不保存', secret: true },
  {
    key: 'personalToken',
    label: 'Personal Token',
    placeholder: '可选，留空不保存',
    secret: true,
  },
  { key: 'apiKey', label: 'API KEY', placeholder: '可选，留空不保存', secret: true },
];

const EMPTY_FORM_VALUES: ServiceSecretFormValues = {
  serviceName: '',
  requestPublicUrl: '',
  requestInternalUrl: '',
  groupName: '',
  tenantName: '',
  applicationName: '',
  appId: '',
  clientId: '',
  accessKey: '',
  secretKey: '',
  personalToken: '',
  apiKey: '',
};

function preferredGroupValue(
  group: SecretGroup,
  key: 'requestPublicUrl' | 'requestInternalUrl',
): string {
  const counts = new Map<string, number>();
  const firstSeen = new Map<string, number>();

  group.items.forEach((item, index) => {
    const value = String(item[key] || '').trim();
    if (!value) return;
    counts.set(value, (counts.get(value) || 0) + 1);
    if (!firstSeen.has(value)) firstSeen.set(value, index);
  });

  let preferred = '';
  let preferredCount = 0;
  let preferredIndex = Number.POSITIVE_INFINITY;
  counts.forEach((count, value) => {
    const index = firstSeen.get(value) ?? Number.POSITIVE_INFINITY;
    if (count > preferredCount || (count === preferredCount && index < preferredIndex)) {
      preferred = value;
      preferredCount = count;
      preferredIndex = index;
    }
  });
  return preferred;
}

function fieldValue(detail: ServiceSecretDetail, key: DetailFieldKey): string {
  return detail[key] || '';
}

function formatSecret(detail: ServiceSecretDetail): string {
  return DETAIL_FIELDS
    .map(({ key, label }) => ({ label, value: fieldValue(detail, key).trim() }))
    .filter(({ value }) => value)
    .map(({ label, value }) => `${label}：${value}`)
    .join('\n');
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('复制失败');
}

function ServiceSecretsTab({ mobile }: Props) {
  const [secrets, setSecrets] = useState<ServiceSecretSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addingFromGroup, setAddingFromGroup] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<ServiceSecretFormValues>();

  const [unlockRequest, setUnlockRequest] = useState<UnlockRequest | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [passwordForm] = Form.useForm<{ password: string }>();

  const [detail, setDetail] = useState<ServiceSecretDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await serviceSecretsGet();
      setSecrets(Array.isArray(data) ? data : []);
    } catch {
      setSecrets([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo<SecretGroup[]>(() => {
    const keyword = search.trim().toLowerCase();
    const filtered = keyword
      ? secrets.filter(
          (secret) =>
            secret.serviceName.toLowerCase().includes(keyword) ||
            RECORD_FIELDS.some(({ key }) =>
              String(secret[key] || '').toLowerCase().includes(keyword),
            ),
        )
      : secrets;

    const groupMap = new Map<string, SecretGroup>();
    filtered.forEach((secret) => {
      const name = secret.serviceName?.trim() || '未命名服务';
      const current = groupMap.get(name);
      if (current) {
        current.items.push(secret);
      } else {
        groupMap.set(name, { key: name, name, items: [secret] });
      }
    });

    return Array.from(groupMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name, 'zh-CN'),
    );
  }, [secrets, search]);

  const allCollapsed =
    groups.length > 0 && groups.every((group) => collapsedGroups.has(group.key));

  const toggleGroup = (key: string) => {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllGroups = () => {
    setCollapsedGroups(
      allCollapsed ? new Set() : new Set(groups.map((group) => group.key)),
    );
  };

  const openAdd = () => {
    setEditingId(null);
    setAddingFromGroup(false);
    form.resetFields();
    form.setFieldsValue(EMPTY_FORM_VALUES);
    setEditorOpen(true);
  };

  const openAddForGroup = (group: SecretGroup) => {
    setEditingId(null);
    setAddingFromGroup(true);
    form.resetFields();
    form.setFieldsValue({
      ...EMPTY_FORM_VALUES,
      serviceName: String(group.items[0]?.serviceName || '').trim(),
      requestPublicUrl: preferredGroupValue(group, 'requestPublicUrl'),
      requestInternalUrl: preferredGroupValue(group, 'requestInternalUrl'),
    });
    setEditorOpen(true);
  };

  const openEdit = (record: ServiceSecretDetail) => {
    setEditingId(record.id);
    setAddingFromGroup(false);
    form.resetFields();
    form.setFieldsValue({
      serviceName: record.serviceName,
      requestPublicUrl: record.requestPublicUrl,
      requestInternalUrl: record.requestInternalUrl,
      groupName: record.groupName,
      tenantName: record.tenantName,
      applicationName: record.applicationName,
      appId: record.appId,
      clientId: record.clientId,
      accessKey: record.accessKey,
      secretKey: record.secretKey,
      personalToken: record.personalToken,
      apiKey: record.apiKey,
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingId(null);
    setAddingFromGroup(false);
    form.resetFields();
  };

  const editorTitle = editingId
    ? '编辑服务密钥'
    : addingFromGroup
      ? '添加服务记录'
      : '添加服务密钥';

  const handleSave = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;

    const payload: ServiceSecretInput = {
      serviceName: values.serviceName || '',
      requestPublicUrl: values.requestPublicUrl || '',
      requestInternalUrl: values.requestInternalUrl || '',
      groupName: values.groupName || '',
      tenantName: values.tenantName || '',
      applicationName: values.applicationName || '',
      appId: values.appId || '',
      clientId: values.clientId || '',
      accessKey: values.accessKey || '',
      secretKey: values.secretKey || '',
      personalToken: values.personalToken || '',
      apiKey: values.apiKey || '',
    };

    setSaving(true);
    try {
      if (editingId) {
        await serviceSecretUpdate(editingId, payload);
        message.success('服务密钥已更新');
      } else {
        await serviceSecretCreate(payload);
        message.success('服务密钥已添加');
      }
      closeEditor();
      await load();
    } catch {
      message.error('保存失败，请稍后再试');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await serviceSecretDelete(id);
      message.success('服务密钥已删除');
      await load();
    } catch {
      message.error('删除失败，请稍后再试');
    }
  };

  const requestUnlock = (record: ServiceSecretSummary, action: UnlockAction) => {
    setUnlockRequest({
      id: record.id,
      name: record.serviceName?.trim() || '未命名服务',
      action,
    });
  };

  const closeUnlock = () => {
    if (unlocking) return;
    setUnlockRequest(null);
    passwordForm.resetFields();
  };

  const handleUnlock = async () => {
    const values = await passwordForm.validateFields().catch(() => null);
    if (!values || !unlockRequest) return;

    setUnlocking(true);
    try {
      const revealed = await serviceSecretReveal(unlockRequest.id, values.password);
      const action = unlockRequest.action;
      setUnlockRequest(null);
      passwordForm.resetFields();

      if (action === 'view') {
        setDetail(revealed);
        setDetailOpen(true);
      } else {
        openEdit(revealed);
      }
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 401) {
        passwordForm.setFields([
          { name: 'password', errors: ['系统密码错误，请重新输入'] },
        ]);
      } else if (status === 429) {
        message.error('验证尝试过于频繁，请稍后再试');
      } else {
        message.error('无法解锁密钥，请稍后再试');
      }
    } finally {
      setUnlocking(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetail(null);
  };

  const handleCopyAll = async () => {
    if (!detail) return;
    try {
      await copyText(formatSecret(detail));
      message.success('完整服务密钥已复制');
    } catch {
      message.error('复制失败，请手动选择内容');
    }
  };

  const handleCopyField = async (label: string, value: string) => {
    try {
      await copyText(value);
      message.success(`${label}已复制`);
    } catch {
      message.error('复制失败，请手动选择内容');
    }
  };

  const editorForm = (
    <Form
      form={form}
      layout="vertical"
      autoComplete="off"
      className={styles.form}
      initialValues={EMPTY_FORM_VALUES}
    >
      <div className={styles.formSectionTitle}>服务信息</div>
      <div className={styles.formGrid}>
        {FORM_FIELDS.filter((field) => !field.secret).map((field) => (
          <Form.Item key={field.key} name={field.key} label={field.label}>
            <Input
              allowClear
              spellCheck={false}
              className={field.mono ? styles.monoInput : undefined}
              placeholder={field.placeholder}
            />
          </Form.Item>
        ))}
      </div>

      <div className={styles.formSectionTitle}>
        访问凭据
        <span>仅保存实际填写的内容</span>
      </div>
      <div className={styles.formGrid}>
        {FORM_FIELDS.filter((field) => field.secret).map((field) => (
          <Form.Item key={field.key} name={field.key} label={field.label}>
            <Input.Password
              spellCheck={false}
              autoComplete="new-password"
              className={styles.monoInput}
              placeholder={field.placeholder}
            />
          </Form.Item>
        ))}
      </div>
    </Form>
  );

  const editorFooter = (
    <div className={styles.drawerFooter}>
      <Button className={styles.secondaryBtn} onClick={closeEditor} disabled={saving}>
        取消
      </Button>
      <Button
        type="primary"
        className={styles.primaryBtn}
        loading={saving}
        onClick={() => void handleSave()}
      >
        保存服务密钥
      </Button>
    </div>
  );

  const detailFields = detail
    ? DETAIL_FIELDS
        .map((field) => ({
          ...field,
          value: fieldValue(detail, field.key).trim(),
        }))
        .filter((field) => field.value)
    : [];

  const detailContent = (
    <div className={styles.detailBody}>
      <div className={styles.detailNotice}>
        <SafetyCertificateOutlined />
        <span>完整密钥仅在当前页面内存中展示，关闭后将清除。</span>
      </div>
      <dl className={styles.detailList}>
        {detailFields.map((field) => (
          <div key={field.key} className={styles.detailRow}>
            <dt>
              <span>{field.label}</span>
              {SECRET_FIELDS.some((secretField) => secretField.key === field.key) && (
                <LockOutlined aria-label="敏感字段" />
              )}
            </dt>
            <dd>
              <code className={field.mono ? styles.monoValue : undefined}>
                {field.value}
              </code>
              <Tooltip title={`复制${field.label}`}>
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  aria-label={`复制${field.label}`}
                  className={styles.copyFieldBtn}
                  onClick={() => void handleCopyField(field.label, field.value)}
                />
              </Tooltip>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );

  const detailFooter = (
    <div className={styles.drawerFooter}>
      <Button className={styles.secondaryBtn} onClick={closeDetail}>
        关闭
      </Button>
      <Button
        type="primary"
        icon={<CopyOutlined />}
        className={styles.primaryBtn}
        onClick={() => void handleCopyAll()}
      >
        复制完整信息
      </Button>
    </div>
  );

  return (
    <ConfigProvider
      theme={{
        algorithm: antdTheme.darkAlgorithm,
        token: {
          colorPrimary: '#00ffff',
          colorInfo: '#00ffff',
          colorBgContainer: 'rgba(6, 20, 38, 0.96)',
          colorBgElevated: 'rgba(6, 20, 38, 0.98)',
          colorBorder: 'rgba(0, 255, 255, 0.2)',
          colorBorderSecondary: 'rgba(0, 255, 255, 0.12)',
          fontFamily: "'PingFang SC', 'Microsoft YaHei', sans-serif",
          borderRadius: 6,
        },
      }}
    >
      <div className={styles.container}>
      <div className={styles.toolbar}>
        <Input
          prefix={<SearchOutlined className={styles.searchIcon} />}
          placeholder="搜索服务、应用、租户或地址"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          allowClear
          className={styles.searchInput}
          aria-label="搜索服务密钥"
        />
        <div className={styles.toolbarActions}>
          {groups.length > 0 && (
            <Button className={styles.secondaryBtn} onClick={toggleAllGroups}>
              {allCollapsed ? '展开全部' : '收起全部'}
            </Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className={styles.primaryBtn}
            onClick={openAdd}
          >
            {mobile ? '添加' : '添加服务密钥'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className={styles.skeletonList} aria-label="正在加载服务密钥">
          {[0, 1, 2].map((item) => (
            <div key={item} className={styles.skeletonBlock}>
              <Skeleton active title paragraph={{ rows: 3 }} />
            </div>
          ))}
        </div>
      ) : loadError ? (
        <div className={styles.statePanel} role="alert">
          <ReloadOutlined className={styles.stateIcon} />
          <strong>服务密钥加载失败</strong>
          <span>请检查网络或稍后重试。</span>
          <Button icon={<ReloadOutlined />} onClick={() => void load()}>
            重新加载
          </Button>
        </div>
      ) : secrets.length === 0 ? (
        <div className={styles.statePanel}>
          <KeyOutlined className={styles.stateIcon} />
          <strong>还没有保存服务密钥</strong>
          <span>添加第三方服务的地址、租户与访问凭据，之后可按服务名称快速查找。</span>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            添加第一条服务密钥
          </Button>
        </div>
      ) : groups.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="没有匹配的服务密钥"
          className={styles.emptySearch}
        />
      ) : (
        <div className={styles.groupList}>
          {groups.map((group) => {
            const isCollapsed = collapsedGroups.has(group.key);
            return (
              <section key={group.key} className={styles.group}>
                <div className={styles.groupHeader}>
                  <button
                    type="button"
                    className={styles.groupToggle}
                    onClick={() => toggleGroup(group.key)}
                    aria-expanded={!isCollapsed}
                  >
                    <span className={styles.groupHeading}>
                      <KeyOutlined className={styles.groupIcon} />
                      <span className={styles.groupName}>{group.name}</span>
                      <span className={styles.groupCount}>{group.items.length} 条</span>
                    </span>
                    <DownOutlined
                      className={`${styles.chevron} ${isCollapsed ? styles.chevronCollapsed : ''}`}
                      aria-hidden="true"
                    />
                  </button>
                  <Tooltip title="添加记录">
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      className={styles.groupAddBtn}
                      aria-label={`为${group.name}添加记录`}
                      onClick={() => openAddForGroup(group)}
                    />
                  </Tooltip>
                </div>

                {!isCollapsed && (
                  <div className={styles.groupBody}>
                    {group.items.map((secret, index) => (
                      <article
                        key={secret.id}
                        className={styles.record}
                        aria-label={`${group.name}记录 ${index + 1}`}
                      >
                        <div className={styles.recordHeader}>
                          <span className={styles.recordIndex}>
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div className={styles.recordActions}>
                            <Button
                              type="text"
                              icon={<EyeOutlined />}
                              className={styles.viewBtn}
                              onClick={() => requestUnlock(secret, 'view')}
                              aria-label="验证密码并查看完整信息"
                            >
                              {!mobile && '查看'}
                            </Button>
                            <Tooltip title="验证密码并编辑">
                              <Button
                                type="text"
                                icon={<EditOutlined />}
                                className={styles.iconBtn}
                                onClick={() => requestUnlock(secret, 'edit')}
                                aria-label="验证密码并编辑"
                              />
                            </Tooltip>
                            <Popconfirm
                              title="删除这条服务密钥？"
                              description="删除后无法恢复。"
                              okText="删除"
                              cancelText="取消"
                              okButtonProps={{ danger: true }}
                              onConfirm={() => void handleDelete(secret.id)}
                            >
                              <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                className={`${styles.iconBtn} ${styles.deleteBtn}`}
                                aria-label="删除服务密钥"
                                title="删除"
                              />
                            </Popconfirm>
                          </div>
                        </div>

                        <dl className={styles.fieldGrid}>
                          {RECORD_FIELDS.map((field) => {
                            const value = String(secret[field.key] || '').trim();
                            if (!value) return null;
                            return (
                              <div key={field.key} className={styles.fieldItem}>
                                <dt>{field.label}</dt>
                                <dd
                                  className={field.mono ? styles.monoValue : undefined}
                                  title={value}
                                >
                                  {value}
                                </dd>
                              </div>
                            );
                          })}
                          {SECRET_FIELDS.map((field) =>
                            secret[field.flag] ? (
                              <div key={field.key} className={styles.fieldItem}>
                                <dt>{field.label}</dt>
                                <dd className={styles.maskedValue} title="已加密保存">
                                  <LockOutlined />
                                  <span>已加密保存</span>
                                </dd>
                              </div>
                            ) : null,
                          )}
                        </dl>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <Modal
        open={Boolean(unlockRequest)}
        title="验证系统密码"
        okText={unlockRequest?.action === 'edit' ? '验证并编辑' : '验证并查看'}
        cancelText="取消"
        confirmLoading={unlocking}
        maskClosable={!unlocking}
        closable={!unlocking}
        onOk={() => void handleUnlock()}
        onCancel={closeUnlock}
        afterClose={() => passwordForm.resetFields()}
        width={mobile ? '94vw' : 440}
        destroyOnClose
      >
        <div className={styles.unlockIntro}>
          <SafetyCertificateOutlined />
          <div>
            <strong>{unlockRequest?.name}</strong>
            <span>请输入当前 AdenPassport 密码。验证通过后才会解密并返回完整密钥。</span>
          </div>
        </div>
        <Form form={passwordForm} layout="vertical" name="service-secret-unlock">
          <Form.Item
            name="password"
            label="系统密码"
            rules={[{ required: true, message: '请输入系统密码' }]}
          >
            <Input.Password
              autoFocus
              autoComplete="current-password"
              placeholder="输入当前系统的登录密码"
              onPressEnter={() => {
                if (!unlocking) void handleUnlock();
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {mobile ? (
        <>
          <Drawer
            open={editorOpen}
            title={editorTitle}
            placement="bottom"
            height="92%"
            width="100%"
            onClose={closeEditor}
            footer={editorFooter}
            rootClassName={styles.editorDrawer}
            maskClosable={!saving}
            closable={!saving}
          >
            {editorForm}
          </Drawer>
          <Drawer
            open={detailOpen}
            title="服务密钥完整信息"
            placement="bottom"
            height="88%"
            width="100%"
            onClose={closeDetail}
            footer={detailFooter}
            rootClassName={styles.detailDrawer}
          >
            {detailContent}
          </Drawer>
        </>
      ) : (
        <>
          <Modal
            open={editorOpen}
            title={editorTitle}
            width={760}
            onCancel={closeEditor}
            footer={editorFooter}
            maskClosable={!saving}
            closable={!saving}
          >
            {editorForm}
          </Modal>
          <Modal
            open={detailOpen}
            title="服务密钥完整信息"
            width={720}
            onCancel={closeDetail}
            footer={detailFooter}
          >
            {detailContent}
          </Modal>
        </>
      )}
      </div>
    </ConfigProvider>
  );
}

export default ServiceSecretsTab;
